import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, 
  InputAdornment, Tabs, Tab, List, ListItem, ListItemAvatar,
  ListItemText, Avatar, Chip, IconButton, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Schedule';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ReferralProgram: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [inviteMethod, setInviteMethod] = useState<'sms' | 'email'>('email');
  const [inviteValue, setInviteValue] = useState('');
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  // Mock referral data
  const referralCode = `BOTALA-${user?.username?.toUpperCase() || 'USER'}123`;
  const referralLink = `https://botala.app/join?ref=${referralCode}`;
  
  const [referrals, setReferrals] = useState([
    { 
      id: 1, 
      name: 'Sarah Chen', 
      status: 'active', 
      joinedDate: '2024-01-15', 
      activitiesCompleted: 3,
      xpEarned: 150
    },
    { 
      id: 2, 
      name: 'Mike Johnson', 
      status: 'pending', 
      invitedDate: '2024-01-18', 
      method: 'email'
    },
    { 
      id: 3, 
      name: 'Emma Wilson', 
      status: 'active', 
      joinedDate: '2024-01-10', 
      activitiesCompleted: 5,
      xpEarned: 150
    },
  ]);

  const totalXPEarned = referrals
    .filter(r => r.status === 'active')
    .reduce((acc, r) => acc + (r.xpEarned || 0), 0);

  const handleSendInvite = async () => {
    if (!inviteValue) {
      toast.error('Please enter an email or phone number');
      return;
    }

    try {
      const payload = {
        method: inviteMethod,
        value: inviteValue,
        referralCode
      };

      // Mock API call
      // await api.post('/referrals/invite', payload);
      
      toast.success(`Invitation sent! You'll earn 50 XP when they join and complete 3 activities`);
      setInviteValue('');
      
      // Add to pending referrals
      setReferrals([...referrals, {
        id: Date.now(),
        name: inviteValue,
        status: 'pending',
        invitedDate: new Date().toISOString().split('T')[0],
        method: inviteMethod
      }]);
    } catch (error) {
      toast.error('Failed to send invitation');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: '🌱 Join Botala!',
          text: 'Start your gardening journey with me on Botala and we both earn rewards!',
          url: referralLink
        });
      } catch (error) {
        console.log('Share failed:', error);
      }
    } else {
      setOpenShareDialog(true);
    }
  };

  return (
    <Box>
      {/* Stats Overview */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: 'white' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your Referral Rewards
          </Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h3" fontWeight="bold">
                {totalXPEarned} XP
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total earned from referrals
              </Typography>
            </Box>
            <EmojiEventsIcon sx={{ fontSize: 60, opacity: 0.3 }} />
          </Box>
          <Box display="flex" gap={3} mt={2}>
            <Box>
              <Typography variant="h6">{referrals.filter(r => r.status === 'active').length}</Typography>
              <Typography variant="body2">Active Friends</Typography>
            </Box>
            <Box>
              <Typography variant="h6">{referrals.filter(r => r.status === 'pending').length}</Typography>
              <Typography variant="body2">Pending Invites</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Referral Link */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your Referral Link
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              fullWidth
              value={referralLink}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyLink}>
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button 
              variant="contained" 
              startIcon={<ShareIcon />}
              onClick={handleShare}
            >
              Share
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Share this link to earn 50 XP when friends join and complete 3 activities!
          </Typography>
        </CardContent>
      </Card>

      {/* Invite Methods */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Invite Friends
          </Typography>
          <Tabs value={inviteMethod === 'email' ? 0 : 1} onChange={(_, v) => setInviteMethod(v === 0 ? 'email' : 'sms')}>
            <Tab icon={<EmailIcon />} label="Email" />
            <Tab icon={<PhoneIcon />} label="SMS" />
          </Tabs>
          
          <Box mt={2}>
            <TextField
              fullWidth
              label={inviteMethod === 'email' ? 'Email Address' : 'Phone Number'}
              value={inviteValue}
              onChange={(e) => setInviteValue(e.target.value)}
              placeholder={inviteMethod === 'email' ? 'friend@example.com' : '+1234567890'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {inviteMethod === 'email' ? <EmailIcon /> : <PhoneIcon />}
                  </InputAdornment>
                )
              }}
            />
            <Button 
              fullWidth 
              variant="contained" 
              sx={{ mt: 2 }}
              onClick={handleSendInvite}
            >
              Send Invitation
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your Referrals
          </Typography>
          
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label={`Active (${referrals.filter(r => r.status === 'active').length})`} />
            <Tab label={`Pending (${referrals.filter(r => r.status === 'pending').length})`} />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <List>
              {referrals.filter(r => r.status === 'active').map(referral => (
                <ListItem key={referral.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <CheckCircleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={referral.name}
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          Joined {referral.joinedDate} • {referral.activitiesCompleted} activities completed
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((referral.activitiesCompleted || 0) / 3 * 100, 100)} 
                          sx={{ mt: 1, height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    }
                  />
                  <Chip 
                    label={`+${referral.xpEarned} XP`} 
                    color="success" 
                    variant="outlined"
                  />
                </ListItem>
              ))}
            </List>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <List>
              {referrals.filter(r => r.status === 'pending').map(referral => (
                <ListItem key={referral.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <PendingIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={referral.name}
                    secondary={`Invited via ${referral.method} on ${referral.invitedDate}`}
                  />
                  <Button size="small" variant="outlined">
                    Resend
                  </Button>
                </ListItem>
              ))}
            </List>
          </TabPanel>
        </CardContent>
      </Card>

      {/* How it Works */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            How Referral Rewards Work
          </Typography>
          <List>
            <ListItem>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>1</Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary="Share your link or invite friends"
                secondary="Send invitations via email or SMS"
              />
            </ListItem>
            <ListItem>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>2</Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary="Friend joins Botala"
                secondary="They sign up using your referral link"
              />
            </ListItem>
            <ListItem>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>3</Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary="Both earn 50 XP"
                secondary="When they complete their first 3 activities"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Share Dialog */}
      <Dialog open={openShareDialog} onClose={() => setOpenShareDialog(false)}>
        <DialogTitle>Share Your Referral Link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={`🌱 Join me on Botala!\n\nStart your gardening journey and we both earn rewards!\n\n${referralLink}`}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenShareDialog(false)}>Close</Button>
          <Button onClick={handleCopyLink} variant="contained">
            Copy Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={copied} autoHideDuration={2000} onClose={() => setCopied(false)}>
        <Alert severity="success">Link copied to clipboard!</Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferralProgram;